import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { api } from '../api/client';
import { FadeIn } from '../components/AnimatedPanel';
import type { KorvexGame } from '../types';
import { useAuth } from '../context/AuthContext';

export function DiscoverPanel() {
  const { user } = useAuth();
  const [games, setGames] = useState<KorvexGame[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    void api
      .getPlayableGames()
      .then(({ games: list }) => setGames(list))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load games.'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = games.filter(
    (g) =>
      g.name.toLowerCase().includes(query.toLowerCase()) ||
      g.ownerUsername.toLowerCase().includes(query.toLowerCase()),
  );

  return (
    <div className="space-y-6">
      <FadeIn>
        <h2 className="font-display text-3xl font-bold">Discover</h2>
        <p className="mt-1 text-korvex-muted">Play public games published by the Korvex community.</p>
      </FadeIn>

      <div className="relative">
        <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-korvex-muted" />
        <input
          className="input-field pl-11"
          placeholder="Search games…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {loading && <p className="text-korvex-muted">Loading games…</p>}
      {error && <p className="text-red-400">{error}</p>}

      {!loading && !error && filtered.length === 0 && (
        <div className="panel-card text-center text-korvex-muted">
          No public games yet. Publish one from Korvex Studio!
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((game, i) => (
          <FadeIn key={game.id} delay={i * 0.04}>
            <GameCard game={game} isOwner={user?.id === game.ownerId} />
          </FadeIn>
        ))}
      </div>
    </div>
  );
}

function GameCard({ game, isOwner }: { game: KorvexGame; isOwner: boolean }) {
  return (
    <div className="panel-card">
      <div className="mb-4 aspect-video rounded-xl bg-gradient-to-br from-korvex-violet/30 via-korvex-accent/20 to-korvex-pink/20" />
      <h3 className="font-display text-lg font-semibold">{game.name}</h3>
      <p className="text-sm text-korvex-muted">by {game.ownerUsername}</p>
      <p className="mt-2 line-clamp-2 text-sm text-korvex-muted">{game.description}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {game.isPrivate && isOwner && (
          <span className="rounded-full bg-korvex-violet/20 px-2.5 py-1 text-xs text-korvex-violet">Private</span>
        )}
        <span className="rounded-full bg-korvex-accent/15 px-2.5 py-1 text-xs text-korvex-accent">Play in Client</span>
      </div>
    </div>
  );
}
